import { useState } from "react";
import { AdminLayout } from "@/components/layout/AdminLayout";
import { trpc } from "@/lib/trpc";
import { useAuth } from "@/contexts/AuthContext";
import { canWrite } from "@/lib/rbac";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Plus, Search, Edit, UserCheck, UserX, Users, AlertCircle, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { ListPageSkeleton } from "@/components/PageSkeleton";
import { EmptyState } from "@/components/shared/EmptyState";

export default function Partners() {
  const { user } = useAuth();
  const canAct = canWrite(user?.staffRole);
  const [search, setSearch] = useState("");
  const [activeFilter, setActiveFilter] = useState<boolean | undefined>(undefined);
  const [selectedZone, setSelectedZone] = useState<string>("");
  const [page, setPage] = useState(1);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editingPartner, setEditingPartner] = useState<any>(null);

  const { data, isLoading, isError, refetch } = trpc.admin.partners.list.useQuery({
    page,
    limit: 20,
    search: search || undefined,
    active: activeFilter,
    zone: selectedZone || undefined,
  });

  const createMutation = trpc.admin.partners.create.useMutation({
    onSuccess: () => {
      toast.success("Partenaire créé");
      setCreateDialogOpen(false);
      refetch();
    },
    onError: (error) => {
      toast.error(error.message);
    },
  });

  const updateMutation = trpc.admin.partners.update.useMutation({
    onSuccess: () => {
      toast.success("Partenaire mis à jour");
      setEditingPartner(null);
      refetch();
    },
    onError: (error) => {
      toast.error(error.message);
    },
  });

  const handleCreate = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);
    const zones = formData.get("zones")?.toString().split(",").map((s) => s.trim()).filter(Boolean) || [];
    const skills = formData.get("skills")?.toString().split(",").map((s) => s.trim()).filter(Boolean) || [];

    createMutation.mutate({
      name: formData.get("name") as string,
      email: formData.get("email") as string,
      phone: formData.get("phone") as string,
      zones,
      skills,
      dailyCapacity: parseInt(formData.get("dailyCapacity") as string) || 1,
      active: true,
      slaHours: parseInt(formData.get("slaHours") as string) || 24,
      notes: formData.get("notes") as string,
    });
  };

  const handleToggleActive = (partner: any) => {
    updateMutation.mutate({
      id: partner.id,
      active: !partner.active,
    });
  };

  if (isLoading) {
    return (
      <AdminLayout>
        <ListPageSkeleton />
      </AdminLayout>
    );
  }

  if (isError) {
    return (
      <AdminLayout>
        <div className="flex flex-col items-center justify-center min-h-[400px] gap-4">
          <AlertCircle className="h-12 w-12 text-destructive/40" />
          <p className="text-muted-foreground font-medium">Erreur de chargement des partenaires</p>
          <Button variant="outline" onClick={() => refetch()}>
            <RefreshCw className="h-4 w-4 mr-2" />Réessayer
          </Button>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-semibold">Partenaires</h1>
          <p className="text-muted-foreground mt-1">
            Gestion des architectes et rédacteurs mémoire technique
          </p>
        </div>
        <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
          {canAct && (
            <DialogTrigger asChild>
              <Button>
                <Plus className="mr-2 h-4 w-4" />
                Nouveau partenaire
              </Button>
            </DialogTrigger>
          )}
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Créer un partenaire</DialogTitle>
              <DialogDescription>
                Ajoutez un nouveau partenaire (architecte ou rédacteur)
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleCreate}>
              <div className="space-y-4">
                <div>
                  <Label htmlFor="name">Nom *</Label>
                  <Input id="name" name="name" required />
                </div>
                <div>
                  <Label htmlFor="email">Email *</Label>
                  <Input id="email" name="email" type="email" required />
                </div>
                <div>
                  <Label htmlFor="phone">Téléphone</Label>
                  <Input id="phone" name="phone" />
                </div>
                <div>
                  <Label htmlFor="zones">Zones (séparées par des virgules) *</Label>
                  <Input
                    id="zones"
                    name="zones"
                    placeholder="IDF, 92, 93, 94"
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="skills">Compétences (séparées par des virgules) *</Label>
                  <Input
                    id="skills"
                    name="skills"
                    placeholder="CVC, plomberie, électricité, SSI"
                    required
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="dailyCapacity">Capacité quotidienne</Label>
                    <Input
                      id="dailyCapacity"
                      name="dailyCapacity"
                      type="number"
                      defaultValue={1}
                      min={1}
                    />
                  </div>
                  <div>
                    <Label htmlFor="slaHours">SLA (heures)</Label>
                    <Input
                      id="slaHours"
                      name="slaHours"
                      type="number"
                      defaultValue={24}
                      min={1}
                    />
                  </div>
                </div>
                <div>
                  <Label htmlFor="notes">Notes</Label>
                  <textarea
                    id="notes"
                    name="notes"
                    className="w-full min-h-[100px] px-3 py-2 border rounded-md"
                  />
                </div>
              </div>
              <DialogFooter className="mt-6">
                <Button type="button" variant="outline" onClick={() => setCreateDialogOpen(false)}>
                  Annuler
                </Button>
                <Button type="submit" disabled={createMutation.isPending}>
                  Créer
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Filtres */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex gap-4">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" aria-hidden="true" />
                <Input
                  placeholder="Rechercher par nom ou email..."
                  aria-label="Rechercher un partenaire"
                  value={search}
                  onChange={(e) => {
                    setSearch(e.target.value);
                    setPage(1);
                  }}
                  className="pl-10"
                />
              </div>
            </div>
            <select
              className="px-3 py-2 border rounded-md"
              aria-label="Filtrer par statut"
              value={activeFilter === undefined ? "all" : activeFilter ? "active" : "inactive"}
              onChange={(e) => {
                const val = e.target.value;
                setActiveFilter(
                  val === "all" ? undefined : val === "active" ? true : false
                );
                setPage(1);
              }}
            >
              <option value="all">Tous</option>
              <option value="active">Actifs</option>
              <option value="inactive">Inactifs</option>
            </select>
            <Input
              placeholder="Zone (ex: IDF)"
              value={selectedZone}
              onChange={(e) => {
                setSelectedZone(e.target.value);
                setPage(1);
              }}
              className="w-40"
            />
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card>
        <CardHeader>
          <CardTitle>Liste des partenaires</CardTitle>
          <CardDescription>
            {data?.total || 0} partenaire(s) trouvé(s)
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Nom</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>Zones</TableHead>
                <TableHead>Compétences</TableHead>
                <TableHead>Capacité</TableHead>
                <TableHead>SLA</TableHead>
                <TableHead>Projets</TableHead>
                <TableHead>Statut</TableHead>
                <TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {data?.items.length === 0 && (
                <TableRow>
                  <TableCell colSpan={9} className="p-0">
                    <EmptyState
                      icon={<Users className="h-8 w-8" />}
                      title="Aucun partenaire"
                      description={search ? `Aucun résultat pour « ${search} »` : "Aucun partenaire enregistré pour le moment."}
                    />
                  </TableCell>
                </TableRow>
              )}
              {data?.items.map((partner: any) => (
                <TableRow key={partner.id} className="hover:bg-accent transition-colors cursor-pointer">
                  <TableCell className="font-medium">{partner.name}</TableCell>
                  <TableCell>{partner.email}</TableCell>
                  <TableCell>
                    <div className="flex gap-1 flex-wrap">
                      {partner.zones.slice(0, 3).map((zone: string) => (
                        <Badge key={zone} variant="outline">
                          {zone}
                        </Badge>
                      ))}
                      {partner.zones.length > 3 && (
                        <Badge variant="outline">+{partner.zones.length - 3}</Badge>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="flex gap-1 flex-wrap">
                      {partner.skills.slice(0, 3).map((skill: string) => (
                        <Badge key={skill} variant="secondary">
                          {skill}
                        </Badge>
                      ))}
                      {partner.skills.length > 3 && (
                        <Badge variant="secondary">+{partner.skills.length - 3}</Badge>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>{partner.dailyCapacity}/jour</TableCell>
                  <TableCell>{partner.slaHours}h</TableCell>
                  <TableCell>{partner._count?.PartnerAssignment || 0}</TableCell>
                  <TableCell>
                    <Badge variant={partner.active ? "default" : "secondary"}>
                      {partner.active ? "Actif" : "Inactif"}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    {canAct ? (
                      <div className="flex gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          aria-label={partner.active ? "Désactiver le partenaire" : "Activer le partenaire"}
                          onClick={() => handleToggleActive(partner)}
                        >
                          {partner.active ? (
                            <UserX className="h-4 w-4" aria-hidden="true" />
                          ) : (
                            <UserCheck className="h-4 w-4" aria-hidden="true" />
                          )}
                        </Button>
                      </div>
                    ) : (
                      <span className="text-xs text-muted-foreground">—</span>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>

          {data && data.total > data.limit && (
            <div className="flex justify-between items-center mt-4">
              <div className="text-sm text-muted-foreground">
                Page {data.page} sur {Math.ceil(data.total / data.limit)}
              </div>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={page === 1}
                  onClick={() => setPage(page - 1)}
                >
                  Précédent
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={page >= Math.ceil(data.total / data.limit)}
                  onClick={() => setPage(page + 1)}
                >
                  Suivant
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
    </AdminLayout>
  );
}

